import React, { useMemo } from 'react';
import ManCrossPageFooter from '../components/ManCrossPageFooter';
import { computeIvcLevel } from '../shared/managerialUtils';

// ── Color helpers ──────────────────────────────────────────────────────────────

/** Exposure config from backend label or computed fallback (Spec V4 §3.5) */
const getExposureConfig = (expoLabel, ipra, ivcLevel) => {
    const l = (expoLabel || '').toUpperCase();
    if (l === 'EXTREMA' || (!expoLabel && ipra < 65 && ivcLevel >= 1))
        return { label: 'EXTREMA', bg: '#E74C3C', text: '#fff', rank: 4 };
    if (l === 'ALTA'    || (!expoLabel && ipra < 65))
        return { label: 'ALTA',    bg: '#E67E22', text: '#fff', rank: 3 };
    if (l === 'MEDIA'   || (!expoLabel && ipra < 75))
        return { label: 'MEDIA',   bg: '#D4AC0D', text: '#1a1a1a', rank: 2 };
    return { label: 'BAJA', bg: '#62C190', text: '#fff', rank: 1 };
};

/** IVC badge config from nivel + backend label */
const getIvcConfig = (nivel, label) => {
    const l = (label || '').toUpperCase();
    const n = nivel ?? 0;
    if (n === 3 || l.includes('CRÍTICO'))   return { label: 'Riesgo Crítico',  bg: '#E74C3C', text: '#fff'    };
    if (n === 2 || l.includes('ALTO'))      return { label: 'Riesgo Alto',     bg: '#E67E22', text: '#fff'    };
    if (n === 1 || l.includes('MODERADO'))  return { label: 'Riesgo Moderado', bg: '#D4AC0D', text: '#1a1a1a' };
    return { label: 'Riesgo Bajo', bg: '#E8F8F0', text: '#1E7A4E' };
};

/** Grupo badge config: A (rojo) / B (naranja) / C (púrpura) / MIXTO (gris) */
const getGrupoConfig = (grupo) => {
    const g = (grupo || '').toUpperCase();
    if (g === 'A')     return { label: 'A',   bg: '#E74C3C', text: '#fff' };
    if (g === 'B')     return { label: 'B',   bg: '#E67E22', text: '#fff' };
    if (g === 'C')     return { label: 'C',   bg: '#6C4BA6', text: '#fff' };
    if (g === 'MIXTO') return { label: 'MIX', bg: '#8E9BAE', text: '#fff' };
    return null;
};

const getIpraConfig = (ipra) => {
    if (ipra < 65) return { bg: '#F3202A', text: '#fff', weight: 700 };
    if (ipra < 75) return { bg: '#F48732', text: '#fff', weight: 700 };
    return         { bg: 'transparent', text: '#617993', weight: 400 };
};

/** Extracts shortest meaningful label after the D{N} code token */
const shortDimLabel = (title) => {
    if (!title) return '—';
    const raw      = title.split('/')[0].trim();
    const words    = raw.split(/\s+/).filter(Boolean);
    const codeIdx  = words.findIndex(w => /^D\d+$/i.test(w));
    const after    = words.slice(codeIdx >= 0 ? codeIdx + 1 : 0);
    const word     = after.find(w => /[a-záéíóúüñA-ZÁÉÍÓÚÜÑ]/.test(w)) || words[0] || '—';
    return word.length > 9 ? `${word.slice(0, 8)}.` : word;
};

// ── Component ──────────────────────────────────────────────────────────────────

const ManCrossPageRankingIPRA = ({ reportData, headerText, pageNumber }) => {
    const payload      = reportData?.payload || {};
    const ipraTotals   = payload?.totals?.ipra  || {};
    const iashTotals   = payload?.totals?.iash  || {};
    const criticalSups = payload?.critical_supervisors || [];

    // Build area → IVC level map
    const areaIvcMap = useMemo(() => {
        const iashMap = {};
        (iashTotals?.average_by_management_area || []).forEach(a => { iashMap[a.name] = a.average; });
        const ipraMap = {};
        (ipraTotals?.average_by_management_area || []).forEach(a => { ipraMap[a.name] = a.average; });
        const map = {};
        new Set([...Object.keys(iashMap), ...Object.keys(ipraMap)]).forEach(name => {
            map[name] = computeIvcLevel(ipraMap[name] ?? null, iashMap[name] ?? null);
        });
        return map;
    }, [iashTotals, ipraTotals]);

    // Enrich supervisors: use backend values, fallback to local computation
    const rows = useMemo(() => {
        return criticalSups
            .map(sup => {
                const ipra = sup.ipra_score ?? null;
                if (ipra == null) return null;
                const ivcNivel = sup.ivc_nivel ?? areaIvcMap[sup.management_area] ?? 0;
                const ivcCfg   = getIvcConfig(ivcNivel, sup.ivc_label ?? null);
                const expo     = getExposureConfig(sup.exposicion ?? null, ipra, ivcNivel);
                const grupoCfg = getGrupoConfig(sup.grupo ?? null);
                return { ...sup, ivcNivel, ivcCfg, expo, grupoCfg };
            })
            .filter(Boolean)
            .sort((a, b) => b.expo.rank - a.expo.rank || (a.ipra_score ?? 999) - (b.ipra_score ?? 999))
            .slice(0, 10);
    }, [criticalSups, areaIvcMap]);

    // Callout: exposure share of RIESGO CRÍTICO supervisors (weighted by gap × n_cargo)
    const callout = useMemo(() => {
        const critical  = rows.filter(r => r.expo.rank >= 4);
        const expScore  = r => Math.max(0, 75 - (r.ipra_score ?? 75)) * Math.max(1, r.n_cargo ?? 1);
        const totalExp  = rows.reduce((s, r) => s + expScore(r), 0);
        const critExp   = critical.reduce((s, r) => s + expScore(r), 0);
        const pct       = totalExp > 0 ? Math.round((critExp / totalExp) * 100) : 0;
        const n         = critical.length;
        if (n > 0)
            return `${n} supervisor${n > 1 ? 'es' : ''} concentra${n > 1 ? 'n' : ''} el ${pct}% de la exposición total al riesgo — intervención no es negociable.`;
        if (rows.length > 0)
            return `${rows.length} supervisores en zona de riesgo IPRA — priorizar intervención inmediata.`;
        return 'Sin supervisores en zona de riesgo en esta medición.';
    }, [rows]);

    // Footer: most critical supervisor
    const topSup     = rows[0];
    const footerText = topSup
        ? `${topSup.code} con IPRA=${Math.round(topSup.ipra_score ?? 0)} lidera ${topSup.n_cargo ?? '—'} personas. Cada punto de mejora en su IPRA beneficia directamente a ${topSup.n_cargo ?? '—'} trabajadores.`
        : '';

    return (
        <div className="mgcr-rk-page">

            {/* Header bar */}
            <div className="mgcr-page-header">
                <span className="mgcr-page-header-text">{headerText}</span>
                <span className="mgcr-page-header-num">{pageNumber}</span>
            </div>

            <div className="keep-blankspace" />
            
            {/* Title */}
            <div className="mgcr-dx-title-area">
                <h2 className="mgcr-dx-main-title">Ranking de Líderes</h2>
                <p  className="mgcr-dx-subtitle">Exposición por Supervisor</p>
            </div>

            {/* Board: table left + rules right */}
            <div className="mgcr-rk-board">

                {/* LEFT: ranking table */}
                <div className="mgcr-rk-left">

                    {/* Callout */}
                    <div className="mgcr-rk-callout-row">
                        <div className="mgcr-rk-section-label">Tabla de<br />Exposición</div>
                        <div className="mgcr-rk-callout-text">{callout}</div>
                    </div>

                    {/* Table */}
                    <div className="mgcr-rk-table">

                        {/* Header row */}
                        <div className="mgcr-rk-row mgcr-rk-head">
                            <div className="mgcr-rk-cell mgcr-rk-c-code">Código</div>
                            <div className="mgcr-rk-cell mgcr-rk-c-area">Área</div>
                            <div className="mgcr-rk-cell mgcr-rk-c-ipra">IPRA</div>
                            <div className="mgcr-rk-cell mgcr-rk-c-dim">Dim. Crítica</div>
                            <div className="mgcr-rk-cell mgcr-rk-c-cargo">N° Cargo</div>
                            <div className="mgcr-rk-cell mgcr-rk-c-grupo">Grupo</div>
                            <div className="mgcr-rk-cell mgcr-rk-c-ivc">IVC Área</div>
                            <div className="mgcr-rk-cell mgcr-rk-c-expo">Exposición</div>
                        </div>

                        {/* Data rows */}
                        {rows.map((sup, idx) => {
                            const ipraCfg = getIpraConfig(sup.ipra_score ?? 100);
                            return (
                                <div
                                    key={sup.code}
                                    className={`mgcr-rk-row${idx % 2 === 0 ? ' mgcr-rk-row-even' : ''}`}
                                >
                                    <div className="mgcr-rk-cell mgcr-rk-c-code">
                                        <strong>{sup.code}</strong>
                                    </div>
                                    <div className="mgcr-rk-cell mgcr-rk-c-area">
                                        {sup.management_area || '—'}
                                    </div>
                                    <div className="mgcr-rk-cell mgcr-rk-c-ipra">
                                        <span
                                            className="mgcr-rk-badge"
                                            style={{ background: ipraCfg.bg, color: ipraCfg.text, fontWeight: ipraCfg.weight }}
                                        >
                                            {sup.ipra_score != null ? Math.round(sup.ipra_score) : '—'}
                                        </span>
                                    </div>
                                    <div className="mgcr-rk-cell mgcr-rk-c-dim">
                                        {shortDimLabel(sup.worst_dimension?.title)}
                                    </div>
                                    <div className="mgcr-rk-cell mgcr-rk-c-cargo">
                                        {sup.n_cargo ?? '—'}
                                    </div>
                                    <div className="mgcr-rk-cell mgcr-rk-c-grupo">
                                        {sup.grupoCfg
                                            ? <span className="mgcr-rk-badge" style={{ background: sup.grupoCfg.bg, color: sup.grupoCfg.text }}>{sup.grupoCfg.label}</span>
                                            : <span style={{ color: '#aaa' }}>—</span>
                                        }
                                    </div>
                                    <div className="mgcr-rk-cell mgcr-rk-c-ivc">
                                        <span
                                            className="mgcr-rk-badge"
                                            style={{ background: sup.ivcCfg.bg, color: sup.ivcCfg.text }}
                                        >
                                            {sup.ivcCfg.label}
                                        </span>
                                    </div>
                                    <div className="mgcr-rk-cell mgcr-rk-c-expo">
                                        <span
                                            className="mgcr-rk-badge mgcr-rk-expo-badge"
                                            style={{ background: sup.expo.bg, color: sup.expo.text }}
                                        >
                                            {sup.expo.label}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}

                    </div>{/* /table */}
                </div>{/* /left */}

                {/* RIGHT: Regla de Priorización */}
                <div className="mgcr-rk-right">
                    <p className="mgcr-rk-rules-title">Regla de Priorización</p>

                    <div className="mgcr-rk-rule mgcr-rk-rule--red">
                        <p className="mgcr-rk-rule-main">IPRA &lt; 65</p>
                        <p className="mgcr-rk-rule-sub">Zona crítica — sin tolerancia</p>
                    </div>

                    <div className="mgcr-rk-rule mgcr-rk-rule--soft">
                        <p className="mgcr-rk-rule-main">+ N° cargo &gt; 25</p>
                        <p className="mgcr-rk-rule-sub">Exposición amplificada</p>
                    </div>

                    <div className="mgcr-rk-rule mgcr-rk-rule--soft">
                        <p className="mgcr-rk-rule-main">+ IVC activo</p>
                        <p className="mgcr-rk-rule-sub">Co-falla de barreras</p>
                    </div>

                    <div className="mgcr-rk-rule mgcr-rk-rule--result">
                        <p className="mgcr-rk-rule-result-main">= INTERVENCIÓN EXTREMA</p>
                        <p className="mgcr-rk-rule-sub">Esta semana, sin excepción</p>
                    </div>
                </div>{/* /right */}

            </div>{/* /board */}

            {/* Footer insight */}
            {footerText && (
                <div className="mgcr-insight-footer">
                    <strong>Esto implica que:</strong>{' '}{footerText}
                </div>
            )}

            <div className="keep-blankspace" />

            <ManCrossPageFooter />
        </div>
    );
};

export default ManCrossPageRankingIPRA;
